import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditPlanDialog } from '@/features/plans/EditPlanDialog';
import { StatusDialog } from '@/features/plans/StatusDialog';
import {
  formatCents,
  usePlans,
  type PlansResponse,
  type TierSummary,
  type UnbilledDevice,
} from '@/features/plans/usePlans';
import { useTenants, type AdminTenant } from '@/features/tenants/useTenants';

/**
 * Plans & billing — canvas `5f`/`6i` (slice 20).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE ARTBOARD DRAWS AND THIS SCREEN DOES NOT BUILD, AND WHY.
 * ─────────────────────────────────────────────────────────────────────────────
 * `5f` shows a payment-reminders panel with rows like *"Juba Transport · $168 ·
 * 12 d late · EVC+ auto-retry · Remind again"*. Every element of that needs an
 * INVOICE — an amount, a due date, a payment attempt — and `invoices` holds zero
 * rows with nothing in the codebase writing to it. Billing is Phase 3B. There is
 * also no SMS, WhatsApp or EVC+ integration anywhere in the product, so a
 * "Remind again" button would send nothing.
 *
 * So no such control ships — not disabled, not "coming soon". What ships is the
 * part of the ladder that is REAL: the tenants actually in `past_due` or
 * `suspended`, and the soft suspend itself, which `tenancy/middleware.ts`
 * enforces today. The ladder's schedule is rendered as stated policy, which is
 * exactly what it currently is.
 *
 * THE PRICES ARE `RAAD_MASTER_PLAN.md` §7, not the artboard's Bilow/Ganacsi/
 * Shirkad — Mahad, 24 Aug 2026. They arrive from the API as integer cents and
 * are divided by 100 in exactly one place, `formatCents`.
 */

function Panel({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section className="mt-6 rounded-panel border border-raad-border bg-raad-panel p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function TierCard({ tier }: { tier: TierSummary }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-panel border border-raad-border bg-raad-panel p-4">
      <p className="text-[13px] font-semibold">{t(`plans.tier.${tier.tier}`)}</p>
      <p className="mt-1 text-[20px] font-bold num text-raad-accent">
        {formatCents(tier.cents)}
        <span className="ms-1 text-[12px] font-medium text-raad-muted">
          {t('plans.perVehicleMonth')}
        </span>
      </p>
      <p className="mt-2 text-[12.5px] text-raad-muted">
        {t('plans.tierCounts', { tenants: tier.tenantCount, vehicles: tier.vehicleCount })}
      </p>
    </div>
  );
}

/** Cents → `$6.00`; `null` is an explicit "no rate", never `$0.00`. */
function Rate({ cents }: { cents: number | null }) {
  const { t } = useTranslation();
  if (cents === null) return <span className="text-raad-muted">{t('plans.noRate')}</span>;
  return <span className="num">{`$${(cents / 100).toFixed(2)}`}</span>;
}

function UnbilledRow({
  device,
  onAssign,
}: {
  device: UnbilledDevice;
  onAssign: (tenantId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-raad-border last:border-b-0">
      <td className="py-2.5 pr-4 num">{device.imeiMasked}</td>
      <td className="py-2.5 pr-4">{device.model ?? t('plans.unknownModel')}</td>
      <td className="py-2.5 pr-4">
        {device.tenantName ?? <span className="text-raad-muted">{t('plans.unallocated')}</span>}
      </td>
      <td className="py-2.5">
        {/* The fix differs by reason, so the affordance does too. A tenant with
            no package can be priced right here; an unallocated device needs
            PROVISIONING, which lives on the Tenants screen — so this says where
            to go rather than offering a button that cannot do the job. */}
        {device.reason === 'no_package' && device.tenantId !== null ? (
          <button
            type="button"
            onClick={() => onAssign(device.tenantId!)}
            className="min-h-9 rounded-lg border border-raad-primary/40 px-3 text-[12.5px] font-semibold text-raad-accent transition-colors hover:bg-raad-primary/10"
          >
            {t('plans.assign')}
          </button>
        ) : (
          <span className="text-[12.5px] text-raad-muted">{t('plans.provisionIt')}</span>
        )}
      </td>
    </tr>
  );
}

export function Plans() {
  const { t } = useTranslation();
  const plansQ = usePlans();
  const tenantsQ = useTenants();
  const [editing, setEditing] = useState<AdminTenant | null>(null);
  const [statusFor, setStatusFor] = useState<AdminTenant | null>(null);

  if (plansQ.isPending || tenantsQ.isPending) {
    return <p className="text-sm text-raad-muted">{t('plans.loading')}</p>;
  }
  if (plansQ.isError || tenantsQ.isError) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('plans.error')}
      </p>
    );
  }

  // Defensive against an unexpected response shape, for the reason slice 24b
  // earned: a render throw takes the whole portal down. Treated as an ERROR and
  // never as empty data — "no tiers" would be a claim about the price list.
  const data = plansQ.data as Partial<PlansResponse> | undefined;
  const tiers = Array.isArray(data?.tiers) ? data.tiers : null;
  const tenants = Array.isArray(tenantsQ.data?.tenants) ? tenantsQ.data.tenants : null;
  if (tiers === null || tenants === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('plans.error')}
      </p>
    );
  }

  const unbilled = data?.unbilled;
  const dunning = Array.isArray(data?.dunning) ? data.dunning : [];
  const unassignedTenants = data?.unassignedTenantCount ?? 0;

  const tenantById = (id: string) => tenants.find((x) => x.id === id) ?? null;

  return (
    <section aria-labelledby="plans-heading">
      <h1 id="plans-heading" className="font-display text-xl font-bold tracking-tight">
        {t('plans.title')}
      </h1>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {tiers.map((tier) => (
          <TierCard key={tier.tier} tier={tier} />
        ))}
      </div>

      {unassignedTenants > 0 ? (
        // 🔴 NOT a fourth tier card. These tenants are on whatever `plan`
        // defaults to, which nobody chose — counting them under Personal would
        // report a customer base that does not exist.
        <p className="mt-3 text-[12.5px] text-raad-muted">
          {t('plans.unassignedNote', {
            tenants: unassignedTenants,
            vehicles: data?.unassignedVehicleCount ?? 0,
          })}
        </p>
      ) : null}

      <Panel title={t('plans.packages')}>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('plans.col.tenant')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('plans.col.plan')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-right font-semibold">
                  {t('plans.col.rate')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('plans.col.status')}
                </th>
                <th scope="col" className="py-2.5 text-left font-semibold">
                  {t('plans.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tn) => (
                <tr key={tn.id} className="border-b border-raad-border last:border-b-0">
                  <td className="py-2.5 pr-4 font-medium">{tn.name}</td>
                  <td className="py-2.5 pr-4">{t(`plans.tier.${tn.plan}`)}</td>
                  <td className="py-2.5 pr-4 text-right">
                    <Rate cents={tn.monthlyRateCents} />
                    {/* 🔴 A RATE THAT DISAGREES WITH ITS TIER IS LABELLED, NOT
                        HIDDEN. Abdishakur pays $12 on a $10 tier — a legacy rate
                        from the old $8–12 band. Rendering it as a bare number
                        makes the card and the screen look broken; saying "on a
                        deal" makes it a fact someone chose. This is also where a
                        promo code will surface when promos land. */}
                    {(() => {
                      const card = tiers.find((x) => x.tier === tn.plan);
                      const isOverride =
                        card !== undefined &&
                        tn.monthlyRateCents !== null &&
                        tn.monthlyRateCents !== card.cents;
                      return isOverride ? (
                        <span
                          data-testid="rate-override"
                          className="mt-0.5 block text-[11px] font-medium text-raad-warn"
                        >
                          {t('plans.rateOverride', { price: formatCents(card.cents) })}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td className="py-2.5 pr-4">{t(`plans.statusName.${tn.status}`)}</td>
                  <td className="py-2.5">
                    <button
                      type="button"
                      onClick={() => setEditing(tn)}
                      className="min-h-9 rounded-lg border border-raad-primary/40 px-3 text-[12.5px] font-semibold text-raad-accent transition-colors hover:bg-raad-primary/10"
                    >
                      {t('plans.editPlan')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatusFor(tn)}
                      className="ms-2 min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold transition-colors hover:bg-raad-primary/10"
                    >
                      {t('plans.changeStatus')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        title={t('plans.reminders')}
        aside={
          dunning.length > 0 ? (
            <span className="rounded-md border border-raad-warn/40 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-raad-warn">
              {t('plans.overdueCount', { count: dunning.length })}
            </span>
          ) : null
        }
      >
        {dunning.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-raad-muted">{t('plans.noneOverdue')}</p>
        ) : (
          <ul className="mt-3 divide-y divide-raad-border">
            {dunning.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm">
                <span className="font-medium">{d.name}</span>
                <Rate cents={d.monthlyRateCents} />
                <span className="text-[12.5px] text-raad-muted">
                  {t('plans.vehicles', { count: d.vehicleCount })}
                </span>
                <span className="ms-auto rounded-md border border-raad-warn/40 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-raad-warn">
                  {t(`plans.statusName.${d.status}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* The ladder as STATED POLICY. It is copy, not behaviour: nothing sends
            a reminder on day 0, +3 or +7 because there is no invoice to remind
            about and no channel to send on. The day-14 suspend is the one rung
            that exists, and it is the button in the table above. */}
        <p className="mt-3 border-t border-raad-border pt-3 text-[11.5px] text-raad-muted">
          {t('plans.ladder')}
        </p>
      </Panel>

      <Panel
        title={t('plans.unbilledTitle')}
        aside={
          Array.isArray(unbilled) && unbilled.length > 0 ? (
            <span className="rounded-md border border-raad-error/40 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-raad-error">
              {unbilled.length}
            </span>
          ) : null
        }
      >
        {/* 🔴 THREE STATES, NOT TWO. `null` is "we cannot see" and must never
            render as "all clear" — the panel's entire value is that an empty
            list can be trusted, and a failed Traccar read would otherwise
            report zero lost revenue at the moment it went blind. */}
        {unbilled === null || unbilled === undefined ? (
          <p role="alert" className="mt-2 text-[12.5px] text-raad-warn">
            {t('plans.unbilledUnavailable')}
          </p>
        ) : unbilled.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-raad-muted">{t('plans.unbilledNone')}</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[30rem] text-sm">
              <thead>
                <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('plans.col.imei')}
                  </th>
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('plans.col.model')}
                  </th>
                  <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                    {t('plans.col.tenant')}
                  </th>
                  <th scope="col" className="py-2.5 text-left font-semibold">
                    {t('plans.col.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {unbilled.map((d) => (
                  <UnbilledRow
                    key={d.traccarDeviceId}
                    device={d}
                    onAssign={(id) => setEditing(tenantById(id))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[11.5px] text-raad-muted">{t('plans.unbilledCaption')}</p>
      </Panel>

      {editing !== null ? (
        <EditPlanDialog tenant={editing} tiers={tiers} onClose={() => setEditing(null)} />
      ) : null}
      {statusFor !== null ? (
        <StatusDialog tenant={statusFor} onClose={() => setStatusFor(null)} />
      ) : null}
    </section>
  );
}

export default Plans;
